import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { AppName, CleanMessage } from "../../../../util/app";
import AddButton from "../../../../partials/Buttons/AddButton";
import NewShortTermAssessment from "./NewShortTermAssessment";
import ShortTermServiceAssessmentList from "./List";
import { GET_SHORT_ASSESSMENT, NEW_SHORT_ASSESSMENT, CONFIRM_SHORT_ASSESSMENT } from "../../../../queries/short-assessment.query";
import { useQuery, useMutation } from "react-apollo";
import { toast } from "react-toastify";
import LoadingSpinner from "../../../../partials/loading";
import { ReadData } from "../../../../providers/Patient.Provider";
import Pagination from "../../../../partials/pagination";
import ShortTermServiceAssessmentDetail from "./Detail";

const ShortTermAssessment = ({ match }) => {
    // local state
    const [short, setShort] = useState(null);
    const [newShort, setNewShort] = useState(false);
    const [page, setPage] = useState(1);
    const [limit] = useState(23);

    // patient id
    const { id } = match.params;
    // get patient object alongside the admission id
    const patient = ReadData();

    const scrollTop = () => {
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
    };

    const { loading, data, fetchMore } = useQuery(GET_SHORT_ASSESSMENT, {
        onError: e => toast.error(CleanMessage(e.message)),
        variables: { patient: id, page, limit }
    });

    const [createShortFunc, { loading: cLoading }] = useMutation(NEW_SHORT_ASSESSMENT, {
        onError: e => toast.error(CleanMessage(e.message)),
        onCompleted: d => {
            if (d.NewShortAssessment) {
                const { message } = d.NewShortAssessment;
                toast.success(message);
                setNewShort(false);
                setShort(null);
                scrollTop();
            }
        },
        update: (caches, { data: { NewShortAssessment } }) => {
            const { GetPatientShortAssessments } = caches.readQuery({
                query: GET_SHORT_ASSESSMENT,
                variables: { patient: id, page, limit }
            });
            GetPatientShortAssessments.docs.unshift(NewShortAssessment.doc);
            caches.writeQuery({
                query: GET_SHORT_ASSESSMENT,
                variables: { patient: id, page, limit },
                data: { GetPatientShortAssessments }
            });
        }
    });

    const title = "Short Term Service Assessment";

    const [confirmFunc, { loading: _Loading }] = useMutation(CONFIRM_SHORT_ASSESSMENT, {
        onError: e => toast.error(CleanMessage(e.message)),
        onCompleted: d => {
            if (d.ConfirmShortAssessment) {
                const { message } = d.ConfirmShortAssessment;
                toast.success(message);
            }
        }
    });

    return (
        <>
            <Helmet>
                <title>
                    {title} | {AppName}
                </title>
            </Helmet>

            <div className="row">
                <div className="col-12">
                    {/* LIST SHORT TERM SERVICE ASSESSMENT */}
                    {!newShort && !short && (
                        <div className="card fade-in">
                            <div className="card-head">
                                <header>{title}</header>
                                <div className="float-right mt-2 mr-2">
                                    <AddButton
                                        title="Add"
                                        onClick={() => {
                                            setNewShort(true);
                                            setShort(null);
                                            scrollTop();
                                        }}
                                    />
                                </div>
                            </div>
                            <div className="card-body" style={{ minHeight: "450px" }}>
                                <LoadingSpinner loading={loading || _Loading} />
                                {data && (
                                    <ShortTermServiceAssessmentList
                                        onView={item => {
                                            setShort(item);
                                            scrollTop();
                                            setNewShort(false);
                                        }}
                                        onConfirm={async item => {
                                            if (window.confirm("Are you sure you want to confirm this assessment?")) {
                                                // confirm here
                                                await confirmFunc({
                                                    variables: { id: item.id }
                                                });
                                            }
                                        }}
                                        items={data.GetPatientShortAssessments.docs || []}
                                    />
                                )}
                            </div>
                            {data && (
                                <div className="card-footer fade-in">
                                    <Pagination
                                        length={data.GetPatientShortAssessments.docs.length}
                                        {...data.GetPatientShortAssessments}
                                        onPageClicked={page =>
                                            setPage(page) ||
                                            fetchMore({
                                                variables: { patient: id, page, limit },
                                                updateQuery: (prev, { fetchMoreResult }) => {
                                                    if (!fetchMoreResult) return prev;
                                                    return {
                                                        GetPatientShortAssessments: fetchMoreResult.GetPatientShortAssessments
                                                    };
                                                }
                                            })
                                        }
                                    />
                                </div>
                            )}
                        </div>
                    )}

                    {/* NEW SHORT TERM SERVICE ASSESSMENT */}
                    {newShort && !short && (
                        <div className="card fade-in">
                            <div className="card-head">
                                <header>
                                    <button
                                        onClick={() => {
                                            setNewShort(false);
                                            setShort(null);
                                        }}
                                        title="Back"
                                        className="mdl-button mdl-button--icon margin-right-10 mdl-button--colored"
                                    >
                                        <i className="material-icons text-primary">keyboard_backspace</i>
                                    </button>
                                    New {title}
                                </header>
                            </div>
                            <div className="card-body">
                                <NewShortTermAssessment
                                    onCancel={() => {
                                        setNewShort(false);
                                        setShort(null);
                                        scrollTop();
                                    }}
                                    onSubmit={async item => {
                                        const { model, confirm } = item;
                                        await createShortFunc({
                                            variables: {
                                                confirm,
                                                model: {
                                                    ...model,
                                                    patient: id,
                                                    admission: patient.lastAdmission
                                                }
                                            }
                                        });
                                    }}
                                />
                                <LoadingSpinner loading={cLoading} />
                            </div>
                        </div>
                    )}

                    {/* DETAIL OF SHORT TERM SERVICE ASSESSMENT */}
                    {!newShort && short && (
                        <div className="card fade-in">
                            <div className="card-head">
                                <header>
                                    <button
                                        onClick={() => {
                                            setNewShort(false);
                                            setShort(null);
                                        }}
                                        title="Back"
                                        className="mdl-button mdl-button--icon margin-right-10 mdl-button--colored"
                                    >
                                        <i className="material-icons text-primary">keyboard_backspace</i>
                                    </button>
                                    {title} Detail Page
                                </header>
                            </div>
                            <div className="card-body">
                                <ShortTermServiceAssessmentDetail item={short} />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default ShortTermAssessment;
